// Lookup Collections Configuration
// Based on docs/LOOKUP_COLLECTIONS_REFERENCE.md

export interface LookupGroup {
  id: string;
  displayName: string;
  sortOrder: number;
}

export interface LookupCollectionConfig {
  collectionName: string;
  displayName: string;
  description: string;
  groupId: string;
  isMultiSelect: boolean;
  // Phase 2 PR1: seed-time-only flags that mirror the LookupCollectionDocument
  // shape. When a collection's Firestore doc is missing on first read,
  // getLookupCollectionFull writes these onto the seeded doc. Once the doc
  // exists these are ignored — the Lookup Manager's "Features" panel is the
  // live source of truth (persists via updateLookupCollectionProperties).
  // Deliberately optional so existing 100+ configs don't need edits; each
  // falls back to the historical default (supportsSynonyms true, others false).
  supportsSynonyms?: boolean;
  supportsMeasurement?: boolean;
  supportsGrouping?: boolean;
}

// Group definitions
export const LOOKUP_GROUPS: LookupGroup[] = [
  { id: 'case_original', displayName: 'Case Details (Original)', sortOrder: 1 },
  {
    id: 'case_extended',
    displayName: 'Case Details (Extended — Curator/Horologist)',
    sortOrder: 2,
  },
  { id: 'dial_original', displayName: 'Dial Details (Original)', sortOrder: 3 },
  {
    id: 'dial_extended',
    displayName: 'Dial Details (Extended — Curator/Horologist)',
    sortOrder: 4,
  },
  {
    id: 'movement_original',
    displayName: 'Movement Details (Original)',
    sortOrder: 5,
  },
  {
    id: 'movement_extended',
    displayName: 'Movement Details (Extended — Curator/Horologist)',
    sortOrder: 6,
  },
  {
    id: 'module_extended',
    displayName: 'Module Details (Extended — Curator/Horologist)',
    sortOrder: 7,
  },
  {
    id: 'pocket_watch',
    displayName: 'Pocket Watch Details',
    sortOrder: 7.5,
  },
  {
    id: 'strap_original',
    displayName: 'Strap & Bracelet Details (Original)',
    sortOrder: 8,
  },
  {
    id: 'strap_extended',
    displayName: 'Strap & Bracelet Details (Extended — Curator/Horologist)',
    sortOrder: 9,
  },
  {
    id: 'general_purchase',
    displayName: 'General / Purchase Details',
    sortOrder: 10,
  },
  { id: 'service_history', displayName: 'Service History', sortOrder: 11 },
  {
    id: 'watch_extras',
    displayName: 'Watch Extras / Components',
    sortOrder: 12,
  },
  { id: 'timing', displayName: 'Timing / Timegrapher', sortOrder: 13 },
  {
    id: 'provenance',
    displayName:
      'Provenance, Authentication & Wearability (Curator/Horologist)',
    sortOrder: 14,
  },
  {
    id: 'collectability',
    displayName: 'Collectability (Curator/Horologist)',
    sortOrder: 15,
  },
  {
    id: 'brand_organization',
    displayName: 'Brand & Organization',
    sortOrder: 16,
  },
  {
    id: 'storage_winder',
    displayName: 'Storage & Watch Winders',
    sortOrder: 17,
  },
  { id: 'uncategorized', displayName: 'Uncategorized', sortOrder: 99 },
];

// All lookup collection configurations
export const LOOKUP_COLLECTIONS_CONFIG: LookupCollectionConfig[] = [
  // Case Details (Original)
  {
    collectionName: 'lookup_case_materials',
    displayName: 'Case Materials',
    description: 'Available materials for watch cases',
    groupId: 'case_original',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_case_shapes',
    displayName: 'Case Shapes',
    description: 'Available case shapes and forms',
    groupId: 'case_original',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_crystal_materials',
    displayName: 'Crystal Materials',
    description:
      'Watch crystal materials AND their features (cyclops lens, domed, flat) — one vocabulary, all values at the same level',
    groupId: 'case_original',
    // Multi-select: a crystal is a material plus its features — a Datejust is
    // ["sapphire_crystal", "cyclops_lens"]. Deliberately ONE collection rather
    // than a material/feature split; revisit if the mixed vocabulary starts to
    // confuse the picker. This flag only takes effect at FIRST seed of a fresh
    // collection doc — the live doc's flag is owned by the Lookup Manager
    // (Features row).
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_crystal_coating',
    displayName: 'Crystal Coating',
    description: 'Coatings applied to watch crystals',
    groupId: 'case_original',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_caseback_type',
    displayName: 'Case Back Type',
    description: 'Types of case backs and their mechanisms',
    groupId: 'case_original',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_bezel_material',
    displayName: 'Bezel Material',
    description: 'Materials used for watch bezels',
    groupId: 'case_original',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_case_condition',
    displayName: 'Case Condition',
    description: 'Condition ratings for watch cases',
    groupId: 'case_original',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_water_resistance',
    displayName: 'Water Resistance Levels',
    description: 'Water resistance ratings and levels',
    groupId: 'case_original',
    isMultiSelect: false,
  },

  // Case Details (Extended — Curator/Horologist)
  {
    collectionName: 'lookup_crown_types',
    displayName: 'Crown Type',
    description:
      'Crown attachment (screw-down, push-pull), sealing system (Twinlock, Triplock) and shape (onion, cabochon) — one vocabulary, all values at the same level',
    groupId: 'case_extended',
    // Multi-select: a crown is several of these at once — a Rolex crown is
    // ["screw_down", "twinlock"]. Deliberately ONE collection rather than
    // attachment/system/shape sub-lookups. This flag only takes effect at FIRST
    // seed of a fresh collection doc — the live doc's flag is owned by the
    // Lookup Manager (Features row).
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_bezel_types',
    displayName: 'Bezel Type',
    description: 'Bezel types and styles',
    groupId: 'case_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_bezel_colors',
    displayName: 'Bezel Color',
    description:
      'Colors present on the bezel or its insert — a bezel routinely has more than one (Pepsi = red + blue)',
    groupId: 'case_extended',
    // Multi-select: a two-tone insert is genuinely two colors, so a single
    // value would force a choice between them or a made-up "red/blue" custom
    // entry. Deliberately its own collection rather than reusing
    // lookup_dial_colors — the seed below starts from the same vocabulary, but
    // bezel-only terms (the Pepsi/Batman-style insert colors, `mixed`) belong
    // here and dial-only ones do not. This flag only takes effect at FIRST seed
    // of a fresh collection doc — the live doc's flag is owned by the Lookup
    // Manager (Features row).
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_case_finishes',
    displayName: 'Case Finish',
    description: 'Surface finish types for watch cases',
    groupId: 'case_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_case_colors',
    displayName: 'Case Color',
    description:
      "The case body's own colour — not the dial and not the bezel insert. Often the only thing separating two otherwise identical references",
    groupId: 'case_extended',
    // Multi-select: a two-tone case is genuinely two colours (a steel case with
    // a gold bezel-side ring), and a single value would force a choice between
    // them. Its OWN collection rather than a reuse of lookup_bezel_colors — a
    // case comes in resins and titanium an insert never does, an insert comes in
    // dive-bezel colours a case never does, and a shared list would force every
    // future addition on one onto the other.
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_case_coatings',
    displayName: 'Case Coating',
    description: 'Protective and decorative coatings applied to watch cases',
    groupId: 'case_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_case_construction',
    displayName: 'Case Construction',
    description: 'Case construction methods and configurations',
    groupId: 'case_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_pusher_types',
    displayName: 'Pusher Type',
    description: 'Types of chronograph pushers and buttons',
    groupId: 'case_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_engraving_wear',
    displayName: 'Engraving Wear',
    description: 'Level of wear on caseback engravings',
    groupId: 'case_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_helium_valve_type',
    displayName: 'Helium Valve Type',
    description:
      'Types of helium escape valves (automatic, manual, integrated)',
    groupId: 'case_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_helium_valve_placement',
    displayName: 'Helium Valve Placement',
    description:
      'Location of helium escape valve on the case (left case, right case, caseback, integrated)',
    groupId: 'case_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_winding_directions',
    displayName: 'Winding Direction',
    description: 'Rotor winding directions for automatic movements',
    groupId: 'case_extended',
    isMultiSelect: false,
  },

  // Dial Details (Original)
  {
    collectionName: 'lookup_dial_colors',
    displayName: 'Dial Colors',
    description: 'Available colors for watch dials',
    groupId: 'dial_original',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_hand_colors',
    displayName: 'Hand Colors',
    description:
      'Available colors for watch hands — often multiple per watch (e.g. silver hour/minute + orange seconds)',
    groupId: 'dial_original',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_index_colors',
    displayName: 'Index Color',
    description:
      'Available colors for hour indices/markers — often multiple per watch (e.g. gold indices + white printing)',
    groupId: 'dial_original',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_hand_types',
    displayName: 'Hand Type',
    description:
      'Functional hand types present on the dial (hour, minute, seconds, GMT, chronograph hands, …) — multi-select',
    groupId: 'dial_original',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_dial_materials',
    displayName: 'Dial Materials',
    description: 'Dial materials used in watches',
    groupId: 'dial_original',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_dial_finish',
    displayName: 'Dial Finish',
    description: 'Dial finish types and styles',
    groupId: 'dial_original',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_dial_styles',
    displayName: 'Dial Styles',
    description: 'Dial style / pattern (Panda, Reverse Panda, Sunburst, Gilt, …)',
    groupId: 'dial_original',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_dial_numerals',
    displayName: 'Dial Numerals',
    description: 'Types of numerals and markers on watch dials',
    groupId: 'dial_original',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_dial_hands',
    displayName: 'Dial Hands',
    description: 'Types of watch hands and styles',
    groupId: 'dial_original',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_dial_subdials',
    displayName: 'Dial Subdials',
    description: 'Types of subdials and their functions',
    groupId: 'dial_original',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_dial_condition',
    displayName: 'Dial Condition',
    description: 'Dial condition ratings and descriptions',
    groupId: 'dial_original',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_date_display',
    displayName: 'Date Display',
    description: 'Date display types and formats',
    groupId: 'dial_original',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_chronograph_type',
    displayName: 'Chronograph Type',
    description: 'Types of chronograph mechanisms and complications',
    groupId: 'dial_original',
    isMultiSelect: false,
  },

  // Dial Details (Extended — Curator/Horologist)
  {
    collectionName: 'lookup_lume_presence',
    displayName: 'Lume Presence',
    description: 'Where luminous material is applied on the dial',
    groupId: 'dial_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_lume_material',
    displayName: 'Lume Material',
    description: 'Types of luminous material used on dials and hands',
    groupId: 'dial_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_lume_day_color',
    displayName: 'Lume Day Color',
    description: 'Daylight color of the luminous material',
    groupId: 'dial_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_lume_glow_color',
    displayName: 'Lume Glow Color',
    description: 'Glow color of the luminous material in the dark',
    groupId: 'dial_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_dial_marker_type',
    displayName: 'Marker Type',
    description: 'Construction type of hour markers on the dial',
    groupId: 'dial_extended',
    // Multi-select: a dial can combine constructions (applied batons with a
    // printed minute track). Backs the ref editor's "Marker Type" field, stored
    // at dialAndHands.indexes. This flag only takes effect at FIRST seed of a
    // fresh collection doc — the live doc's flag is owned by the Lookup Manager
    // (Features row), which is where it was actually turned on.
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_dial_logo_type',
    displayName: 'Logo Type',
    description: 'How the brand logo is applied to the dial',
    groupId: 'dial_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_dial_text_style',
    displayName: 'Dial Text Style',
    description: 'Style and color of text printing on the dial',
    groupId: 'dial_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_dial_patterns',
    displayName: 'Dial Pattern',
    description: 'Surface patterns and textures on the dial',
    groupId: 'dial_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_chapter_ring_type',
    displayName: 'Chapter Ring Type',
    description: 'Type of chapter ring or inner bezel ring',
    groupId: 'dial_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_minute_track_type',
    displayName: 'Minute Track Type',
    description: 'Style of minute track markings on the dial',
    groupId: 'dial_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_dial_scales',
    displayName: 'Dial Scales',
    description: 'Measurement scales present on the dial or bezel',
    groupId: 'dial_extended',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_dial_features',
    displayName: 'Dial Features',
    description:
      'Structural and decorative attributes of the dial — skeletonisation, apertures, inlays, gem setting. Distinct from Dial Finish (surface treatment) and Dial Material (what the dial is made of).',
    groupId: 'dial_extended',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_date_window_frame',
    displayName: 'Date Window Frame',
    description: 'Frame style around the date window aperture',
    groupId: 'dial_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_date_wheel_color',
    displayName: 'Date Wheel Color',
    description: 'Color of the date wheel disc',
    groupId: 'dial_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_date_window_position',
    displayName: 'Date Window Position',
    description: 'Position of the date window on the dial',
    groupId: 'dial_extended',
    isMultiSelect: false,
  },

  // Movement Details (Original)
  {
    collectionName: 'lookup_movement_types',
    displayName: 'Movement Types',
    description: 'Types of watch movements and mechanisms',
    groupId: 'movement_original',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_beat_rate',
    displayName: 'Beat Rate',
    description: 'Beat rates for mechanical watch movements',
    groupId: 'movement_original',
    isMultiSelect: false,
  },

  // Movement Details (Extended — Curator/Horologist)
  {
    collectionName: 'lookup_complications',
    displayName: 'Complications',
    description: 'Watch complications and advanced functions',
    groupId: 'movement_extended',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_movement_manufacturer',
    displayName: 'Movement Manufacturer',
    description: 'Manufacturers and makers of watch movements',
    groupId: 'movement_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_movement_origin',
    displayName: 'Movement Origin',
    description: 'Country of origin for watch movements',
    groupId: 'movement_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_movement_architecture',
    displayName: 'Movement Architecture',
    description: 'Movement architecture and construction type',
    groupId: 'movement_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_winding_architecture',
    displayName: 'Auto Winding Architecture',
    description:
      'Self-winding system topology of an automatic movement (full rotor, micro-rotor, peripheral, bumper, …). Distinct from Rotor Type, which carries bearing/material detail.',
    groupId: 'movement_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_regulation',
    displayName: 'Regulation',
    description: 'Movement regulation methods and systems',
    groupId: 'movement_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_movement_finishing',
    displayName: 'Movement Finishing',
    description: 'Level and quality of movement finishing',
    groupId: 'movement_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_movement_decoration',
    displayName: 'Movement Decoration',
    description: 'Decorative finishing techniques (perlage, Côtes de Genève, anglage, …)',
    groupId: 'movement_extended',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_plate_finishing',
    displayName: 'Plate Finishing',
    description:
      "The mainplate's PRIMARY surface treatment — the one that defines its look (sunburst, frosted, sandblasted, perlage, Côtes de Genève). Narrower than Movement Finishing, which grades the movement's overall finishing quality; every other technique present belongs in Movement Decoration.",
    groupId: 'movement_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_rotor_material',
    displayName: 'Rotor Material',
    description:
      'What the automatic winding rotor / oscillating weight is MADE OF. Distinct from Auto Winding Architecture (where the mass sits) and the free-text rotor type (its bearing/suspension).',
    groupId: 'movement_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_bridge_material',
    displayName: 'Bridge Material',
    description:
      'What the movement bridges — and the mainplate, where the two share a material — are MADE OF. A rhodium PLATING is a treatment over a base metal, so a rhodium-plated brass bridge is Brass here plus the Rhodium Plated flag.',
    groupId: 'movement_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_escapement_type',
    displayName: 'Escapement Type',
    description: 'Types of escapement mechanisms',
    groupId: 'movement_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_balance_type',
    displayName: 'Balance Type',
    description:
      'Balance-wheel attributes across three orthogonal axes: material (Glucydur, silicon, …), construction (variable-inertia, screw balance, …) and regulation architecture (free-sprung). Multi-select — a balance carries at most one from each axis.',
    groupId: 'movement_extended',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_balance_spring',
    displayName: 'Balance Spring',
    description: 'Types of balance springs and hairsprings',
    groupId: 'movement_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_shock_protection',
    displayName: 'Shock Protection',
    description: 'Shock protection systems for movements',
    groupId: 'movement_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_antimagnetic_standard',
    displayName: 'Anti-Magnetic Standard',
    description: 'Anti-magnetic certification standards and ratings',
    groupId: 'movement_extended',
    isMultiSelect: false,
  },

  // Module Details (Extended — Curator/Horologist)
  {
    collectionName: 'lookup_display_layout',
    displayName: 'Display Layout',
    description: 'Layout type of digital module display',
    groupId: 'module_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_viewing_angle',
    displayName: 'Viewing Angle',
    description: 'Viewing angle range of the LCD display',
    groupId: 'module_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_time_systems',
    displayName: 'Time Systems',
    description: 'Time display systems supported by the module',
    groupId: 'module_extended',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_dst_support',
    displayName: 'DST Support',
    description: 'Daylight saving time adjustment support',
    groupId: 'module_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_calendar_type',
    displayName: 'Calendar Type',
    description: 'Type of calendar system in the module',
    groupId: 'module_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_stopwatch_precision',
    displayName: 'Stopwatch Precision',
    description: 'Measurement precision of the stopwatch function',
    groupId: 'module_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_alarm_types',
    displayName: 'Alarm Types',
    description: 'Types of alarms supported by the module',
    groupId: 'module_extended',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_power_source',
    displayName: 'Power Source',
    description: 'Primary power source for the module',
    groupId: 'module_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_sensors',
    displayName: 'Sensors',
    description: 'Sensors built into the module',
    groupId: 'module_extended',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_sync_source',
    displayName: 'Sync Source',
    description: 'Time synchronization sources supported by the module',
    groupId: 'module_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_module_generation',
    displayName: 'Module Generation',
    description: 'Generation or revision of the module',
    groupId: 'module_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_lcd_type',
    displayName: 'LCD Type',
    description: 'LCD display technology types used in digital watches',
    groupId: 'module_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_light_type',
    displayName: 'Light Type',
    description: 'Backlight or illumination types used in digital watches',
    groupId: 'module_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_display_type',
    displayName: 'Display Type',
    description:
      'Display technology of the electronic module (analog, digital LCD, digital LED, ana-digi, …)',
    groupId: 'module_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_battery_type',
    displayName: 'Battery Type',
    description:
      'Battery / cell type powering the electronic module (e.g. CR2025, SR626SW, rechargeable, solar)',
    groupId: 'module_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_display_colors',
    displayName: 'Display Colors',
    description:
      'Per-reference colour of the digital display background / segments (e.g. green LCD, amber LED) — the digital analogue of dial colour',
    groupId: 'module_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_text_colors',
    displayName: 'Text Colors',
    description:
      'Per-reference colour of the text / readout on the digital display',
    groupId: 'module_extended',
    isMultiSelect: false,
  },

  // Pocket Watch Details
  {
    collectionName: 'lookup_pocket_watch_case_style',
    displayName: 'Pocket Watch Case Style',
    description:
      'Case style of the pocket watch (e.g. hunter, open-face, half-hunter)',
    groupId: 'pocket_watch',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_pocket_watch_escapement',
    displayName: 'Pocket Watch Escapement',
    description:
      'Escapement type of the pocket watch movement (e.g. lever, cylinder, verge)',
    groupId: 'pocket_watch',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_pocket_watch_jewel_bearing',
    displayName: 'Pocket Watch Jewel Bearing',
    description:
      'Jewel bearing material used in the pocket watch movement (e.g. ruby, sapphire, garnet)',
    groupId: 'pocket_watch',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_pocket_watch_movement_size',
    displayName: 'Pocket Watch Movement Size',
    description:
      'Lancashire watch size of the pocket watch movement (e.g. 18s, 16s, 12s)',
    groupId: 'pocket_watch',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_pocket_watch_adjustments',
    displayName: 'Pocket Watch Adjustments',
    description:
      'Position and temperature adjustments of the pocket watch movement (multi-select)',
    groupId: 'pocket_watch',
    isMultiSelect: true,
  },

  // Strap & Bracelet Details (Original)
  //lookup_strapbracelet_type
  {
    collectionName: 'lookup_strapbracelet_type',
    displayName: 'Strap Type',
    description: 'Types of strap or bracelet',
    groupId: 'strap_original',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_strap_materials',
    displayName: 'Strap Materials',
    description: 'Available materials for watch straps and bracelets',
    groupId: 'strap_original',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_strap_color',
    displayName: 'Strap Colors',
    description: 'Available colors for watch straps and bracelets',
    groupId: 'strap_original',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_clasp_materials',
    displayName: 'Clasp Materials',
    description: 'Available materials for watch clasps',
    groupId: 'strap_original',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_clasp_type',
    displayName: 'Clasp Types',
    description: 'Types of watch clasps and buckles',
    groupId: 'strap_original',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_bracelet_condition',
    displayName: 'Bracelet/Strap Condition',
    description: 'Condition ratings for bracelets and straps',
    groupId: 'strap_original',
    isMultiSelect: false,
  },

  // Strap & Bracelet Details (Extended — Curator/Horologist)
  {
    collectionName: 'lookup_bracelet_construction',
    displayName: 'Bracelet Construction',
    description:
      'Bracelet link construction styles (e.g., Oyster, Jubilee, President)',
    groupId: 'strap_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_link_type',
    displayName: 'Link Type',
    description: 'Type of bracelet links (solid, folded, mixed)',
    groupId: 'strap_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_end_links',
    displayName: 'End Links',
    description: 'End link types connecting bracelet to case',
    groupId: 'strap_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_integration_type',
    displayName: 'Integration Type',
    description: 'How the strap/bracelet attaches to the case',
    groupId: 'strap_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_adjustment_type',
    displayName: 'Adjustment Type',
    description: 'Methods used to size the bracelet',
    groupId: 'strap_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_micro_adjustment',
    displayName: 'Micro Adjustment',
    description: 'Fine-adjustment mechanisms on the clasp',
    groupId: 'strap_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_clasp_finish',
    displayName: 'Clasp Finish',
    description: 'Surface finish of the clasp',
    groupId: 'strap_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_clasp_security',
    displayName: 'Clasp Security',
    description: 'Clasp security and locking mechanism',
    groupId: 'strap_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_clasp_extension',
    displayName: 'Clasp Extension',
    description: 'Clasp extension types for diver or wetsuit use',
    groupId: 'strap_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_strap_style',
    displayName: 'Strap Style',
    description: 'Strap attachment and construction style',
    groupId: 'strap_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_strap_padding',
    displayName: 'Strap Padding',
    description: 'Padding thickness level of the strap',
    groupId: 'strap_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_lining_material',
    displayName: 'Lining Material',
    description: 'Material used for the strap lining',
    groupId: 'strap_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_edge_finish',
    displayName: 'Edge Finish',
    description: 'Edge finishing method of the strap',
    groupId: 'strap_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_stitching_color',
    displayName: 'Stitching Color',
    description: 'Color of strap stitching',
    groupId: 'strap_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_stretch_level',
    displayName: 'Stretch Level',
    description: 'Amount of stretch or wear on the bracelet',
    groupId: 'strap_extended',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_polishing_history',
    displayName: 'Polishing History',
    description: 'History of polishing performed on the bracelet',
    groupId: 'strap_extended',
    isMultiSelect: false,
  },

  // General / Purchase Details
  {
    collectionName: 'lookup_watch_types',
    displayName: 'Watch Types',
    description: 'Types of watch display and form factor',
    groupId: 'general_purchase',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_watch_functions',
    displayName: 'Watch Functions',
    description: 'Watch functions and complications',
    groupId: 'general_purchase',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_box_papers',
    displayName: 'Box & Papers',
    description: 'Box and documentation items included with watch',
    groupId: 'general_purchase',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_purchase_condition',
    displayName: 'Purchase Condition',
    description: 'Condition at time of purchase',
    groupId: 'general_purchase',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_watch_occasion',
    displayName: 'Watch Occasion',
    description: 'Watch occasion and style categories',
    groupId: 'general_purchase',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_watch_genders',
    displayName: 'Watch Genders',
    description: 'Gender categories for watches',
    groupId: 'general_purchase',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_detailedwatch_condition',
    displayName: 'Detailed Watch Condition',
    description: 'Detailed condition descriptors for watches',
    groupId: 'general_purchase',
    isMultiSelect: true,
  },

  // Service History
  {
    collectionName: 'lookup_service_type',
    displayName: 'Service Type',
    description: 'Types of watch services and maintenance',
    groupId: 'service_history',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_service_provider_type',
    displayName: 'Service Provider Type',
    description: 'Types of service providers',
    groupId: 'service_history',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_service_reason',
    displayName: 'Service Reason',
    description: 'Reasons for servicing a watch',
    groupId: 'service_history',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_service_work_performed',
    displayName: 'Work Performed',
    description: 'Work performed during service',
    groupId: 'service_history',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_case_polish_level',
    displayName: 'Case Polish Level',
    description: 'Level of case polishing performed',
    groupId: 'service_history',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_service_dial_condition',
    displayName: 'Dial Condition (Service)',
    description: 'Condition of dial after service',
    groupId: 'service_history',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_service_hands_condition',
    displayName: 'Hands Condition (Service)',
    description: 'Condition of hands after service',
    groupId: 'service_history',
    isMultiSelect: false,
  },

  // Watch Extras / Components
  {
    collectionName: 'lookup_watch_extra_category',
    displayName: 'Extra Item Category',
    description: 'Categories for extra items and accessories',
    groupId: 'watch_extras',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_item_originality',
    displayName: 'Item Originality',
    description: 'Originality status of items (OEM, aftermarket, etc.)',
    groupId: 'watch_extras',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_item_condition',
    displayName: 'Item Condition',
    description: 'Condition ratings for extra items',
    groupId: 'watch_extras',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_item_acquisition_type',
    displayName: 'Acquisition Type',
    description: 'How an item was acquired',
    groupId: 'watch_extras',
    isMultiSelect: false,
  },

  // Timing / Timegrapher
  {
    collectionName: 'lookup_observed_issue_type',
    displayName: 'Observed Issue Type',
    description: 'Types of issues observed during timegrapher measurements',
    groupId: 'timing',
    isMultiSelect: true,
  },

  // Provenance, Authentication & Wearability (Curator/Horologist)
  {
    collectionName: 'lookup_purchase_source',
    displayName: 'Purchase Source',
    description: 'Where the watch was acquired from',
    groupId: 'provenance',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_warranty_type',
    displayName: 'Warranty Type',
    description: 'Type of warranty included with the watch',
    groupId: 'provenance',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_verified_by',
    displayName: 'Verified By',
    description: 'Who or what verified the watch authenticity',
    groupId: 'provenance',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_auth_confidence',
    displayName: 'Authentication Confidence',
    description: 'Confidence level of authenticity assessment',
    groupId: 'provenance',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_known_replacements',
    displayName: 'Known Replacements',
    description: 'Known replaced parts on the watch',
    groupId: 'provenance',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_wrist_presence',
    displayName: 'Wrist Presence',
    description: 'How the watch wears visually on wrist',
    groupId: 'provenance',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_weight_feel',
    displayName: 'Weight Feel',
    description: 'Perceived weight of the watch on wrist',
    groupId: 'provenance',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_wear_balance',
    displayName: 'Wear Balance',
    description: 'Balance of the watch head on wrist',
    groupId: 'provenance',
    isMultiSelect: false,
  },

  // Collectability (Curator/Horologist)
  {
    collectionName: 'lookup_rarity_level',
    displayName: 'Rarity Level',
    description: 'Relative rarity in the collector market',
    groupId: 'collectability',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_overall_condition',
    displayName: 'Overall Condition',
    description: 'Overall condition rating for a watch',
    groupId: 'watch_extras',
    isMultiSelect: false,
  },

  // Brand & Organization
  {
    collectionName: 'lookup_parent_organization',
    displayName: 'Parent Organization',
    description:
      'Parent organizations / conglomerates that own watch brands and movement manufacturers (e.g., Swatch Group, LVMH, Independent)',
    groupId: 'brand_organization',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_acquisition_types',
    displayName: 'Acquisition Type',
    description:
      'Kind of brand acquisition / lineage link (full brand acquisition, manufacture integrated, predecessor renamed, trademark registration). Drives the Acquisitions editor on brands.',
    groupId: 'brand_organization',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_acquisition_directness',
    displayName: 'Acquisition Directness',
    description:
      'How direct an acquisition link is (direct brand-to-brand, brand-led group acquisition, parent-assigned, etc.). Drives the Acquisitions editor on brands.',
    groupId: 'brand_organization',
    isMultiSelect: false,
  },

  // Storage & Watch Winders (Task #30)
  //
  // One collection backs the RN app's 7-category storage picker; the six
  // `lookup_winder_*` collections back the admin winder catalog editor AND the
  // `enrichWinder` prompt's {{LOOKUP_*}} tokens. The isMultiSelect flag below is
  // the SINGLE control surface for enrichment cardinality: renderLookupTokens
  // (functions/src/aiEnrich.ts) reads it off the seeded doc and heads the token's
  // value list with "return one slug" vs "return an array of slugs", so the
  // prompt text never restates it. Flip a flag here BEFORE the doc is first
  // seeded — once the Firestore doc exists the Lookup Manager's Features panel
  // owns the live value.
  {
    collectionName: 'lookup_storage_locations',
    displayName: 'Storage Locations',
    description:
      'Where an owned watch physically lives — the 7 structured categories behind the RN storage picker. Slugs match the StorageLocationCategory union in @watchkeeper/shared-types; labels, order and icons are resolved from here rather than hardcoded in either client.',
    groupId: 'storage_winder',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_winder_manufacturers',
    displayName: 'Winder Manufacturers',
    description:
      'Controlled vocabulary of watch-winder makes (Wolf, Barrington, Orbita, …). Backs WinderEntry.make. Deliberately a lookup rather than a normalized `winder_makes` collection — winder makes are disjoint from movement_manufacturers and carry no queryable data of their own. Add a make here before creating winders under it.',
    groupId: 'storage_winder',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_winder_rotation_directions',
    displayName: 'Winder Rotation Directions',
    description:
      'Direction(s) a winder turns. Slugs match the WinderEntry.rotationDirection union in @watchkeeper/shared-types — keep them in lockstep, the field is typed, not free slug text.',
    groupId: 'storage_winder',
    isMultiSelect: false,
  },
  {
    collectionName: 'lookup_winder_programs',
    displayName: 'Winder Programs',
    description:
      'Winding programs / cycle modes a winder supports (continuous, intermittent, rest day, sleep cycle, …). Backs WinderEntry.rotationProgramSlugs.',
    groupId: 'storage_winder',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_winder_power_sources',
    displayName: 'Winder Power Sources',
    description:
      'How a winder is powered (AC mains, batteries, USB, dual AC/battery, solar). Backs WinderEntry.powerSourceSlugs — multi because dual-supply winders are the norm above the entry tier.',
    groupId: 'storage_winder',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_winder_materials',
    displayName: 'Winder Materials',
    description:
      'Case / build materials of a winder (wood, leather, carbon fibre, aluminium, glass, …). Backs WinderEntry.materialSlugs — a winder is routinely several at once (a lacquered wood case with a glass lid).',
    groupId: 'storage_winder',
    isMultiSelect: true,
  },
  {
    collectionName: 'lookup_winder_price_tiers',
    displayName: 'Winder Price Tiers',
    description:
      'Market positioning band for a winder (entry, mid, premium, ultra-premium). Backs WinderEntry.priceTierSlug — a coarse tier, deliberately not a price.',
    groupId: 'storage_winder',
    isMultiSelect: false,
  },
];

// Initial seed values for newly-registered lookup collections.
// When a collection's Firestore document is missing on first read, the
// loader writes these as the initial values (preserving order via sortOrder).
// Keyed by collectionName. Only include collections that should be auto-seeded.
export const LOOKUP_INITIAL_VALUES: Record<
  string,
  {
    value: string;
    displayName: string;
    synonyms?: string[];
    properties?: string[];
    /**
     * Explicit position, when array order cannot express the live one.
     *
     * Defaults to array position in the seeder. Set it only where the live
     * collection has drifted — see `lookup_storage_locations`, whose array must
     * follow the shared-types union while live has `at_service` after `other`.
     */
    sortOrder?: number;
    rnQualifier?: string;
    rnReverseLabel?: string;
  }[]
> = {
  // Structural and decorative dial attributes, split off `lookup_dial_finish`
  // (which keeps SURFACE treatments only). The boundary that decides where a
  // value belongs: the whole dial made of something is `dialAndHands.material`;
  // the same thing as an inlay or accent is a feature here. Marker construction
  // stays `indexes` and logo application stays `logoType` — a feature slug for
  // either would record the same fact twice, in two places that can disagree.
  lookup_dial_features: [
    { value: 'skeletonized', displayName: 'Skeletonized', synonyms: ['openworked', 'skeleton'] },
    { value: 'open_heart', displayName: 'Open Heart', synonyms: ['open balance'] },
    { value: 'movement_aperture', displayName: 'Movement Aperture', synonyms: ['exhibition aperture'] },
    { value: 'sandwich_dial', displayName: 'Sandwich Dial', synonyms: ['sandwich'] },
    { value: 'multi_level', displayName: 'Multi-Level', synonyms: ['three dimensional', 'stepped', 'layered'] },
    { value: 'recessed_subdials', displayName: 'Recessed Subdials', synonyms: ['sunken subdials'] },
    { value: 'mother_of_pearl_inlay', displayName: 'Mother-of-Pearl Inlay', synonyms: ['mop inlay'] },
    { value: 'meteorite_inlay', displayName: 'Meteorite Inlay' },
    { value: 'aventurine_inlay', displayName: 'Aventurine Inlay' },
    { value: 'stone_inlay', displayName: 'Stone Inlay', synonyms: ['hardstone', 'onyx inlay', 'malachite inlay', 'lapis inlay'] },
    { value: 'gem_set_indices', displayName: 'Gem-Set Indices', synonyms: ['diamond indices', 'diamond markers'] },
    { value: 'pave_gem_set', displayName: 'Pavé Gem-Set', synonyms: ['pave', 'diamond pave'] },
    { value: 'engraved_decoration', displayName: 'Engraved Decoration', synonyms: ['hand engraved'] },
    { value: 'enamel_artwork', displayName: 'Enamel Artwork', synonyms: ['cloisonne', 'champleve', 'miniature painting'] },
    { value: 'applied_decoration', displayName: 'Applied Decoration', synonyms: ['applique', 'emblem'] },
  ],
  // parentOrg is stored on Brand/MovementManufacturer docs as a free-text
  // display string and consumed downstream (watchlock UI, Algolia facets) by
  // exact match. Seed value === displayName so the picker round-trips the
  // existing data without introducing slug/display drift.
  lookup_parent_organization: [
    { value: 'Swatch Group', displayName: 'Swatch Group' },
    { value: 'LVMH', displayName: 'LVMH' },
    { value: 'Richemont', displayName: 'Richemont' },
    { value: 'Citizen Holdings', displayName: 'Citizen Holdings' },
    { value: 'Seiko Group', displayName: 'Seiko Group' },
    { value: 'Fossil Group', displayName: 'Fossil Group' },
    { value: 'Movado Group', displayName: 'Movado Group' },
    { value: 'Kering', displayName: 'Kering' },
    { value: 'Casio', displayName: 'Casio' },
    { value: 'Independent', displayName: 'Independent' },
  ],
  // Dial STYLE / pattern. EVERY ref carries a dialStyle; `single-color` is the
  // default for plain dials, themed slugs (Panda, Pepsi, …) are exceptions.
  // `synonyms` drive data-driven detection in the watchlock backfill + Shopify
  // importer; `properties` hold the constituent dial-color slugs a themed style
  // implies (empty = the style applies on top of whatever color is present).
  // Auto-seeds the lookup_dial_styles doc the first time an admin opens it.
  // STYLE = a themed dial PATTERN (Panda, Pepsi, …), NOT a surface finish.
  // Sunburst / Gilt / Tapestry / Tropical are finishes and belong in
  // lookup_dial_finish — removed from here so finish and style stop being
  // conflated. Salmon is kept (debatable) as a themed style.
  lookup_dial_styles: [
    { value: 'single-color', displayName: 'Single Color', properties: [] },
    { value: 'panda', displayName: 'Panda', synonyms: ['panda'], properties: ['black', 'white'] },
    { value: 'reverse-panda', displayName: 'Reverse Panda', synonyms: ['reverse panda', 'rp'], properties: ['black', 'white'] },
    { value: 'pepsi', displayName: 'Pepsi', synonyms: ['pepsi'], properties: ['blue', 'red'] },
    { value: 'coke', displayName: 'Coke', synonyms: ['coke'], properties: ['black', 'red'] },
    { value: 'salmon', displayName: 'Salmon', synonyms: ['salmon'], properties: ['pink'] },
  ],
  // Hand colors. Overlap heavily with dial colors but kept as a dedicated
  // collection (James's call) so hand-specific terms (blued, lume) stay pure.
  // Auto-seeds the lookup_hand_colors doc the first time it's opened/read.
  // Extend via the Lookup Manager. Token {{LOOKUP_HAND_COLORS}} can reference
  // these in AI prompts later (wiring not done here).
  lookup_hand_colors: [
    { value: 'black', displayName: 'Black' },
    { value: 'white', displayName: 'White' },
    { value: 'silver', displayName: 'Silver' },
    { value: 'gold', displayName: 'Gold' },
    { value: 'rose-gold', displayName: 'Rose Gold' },
    { value: 'blue', displayName: 'Blue' },
    { value: 'blued', displayName: 'Blued' },
    { value: 'red', displayName: 'Red' },
    { value: 'orange', displayName: 'Orange' },
    { value: 'yellow', displayName: 'Yellow' },
    { value: 'green', displayName: 'Green' },
    { value: 'brown', displayName: 'Brown' },
    { value: 'gray', displayName: 'Gray' },
    { value: 'lume', displayName: 'Lume' },
  ],
  // Bezel colors. Multi-select — a bezel insert is routinely two colors
  // (GMT-Master "Pepsi" = red + blue, "Batman" = blue + black), so this stores
  // an ARRAY of these slugs on WatchReference.bezelColors.
  //
  // Seeded from the dial/hand color vocabulary (same slugs, so a value means
  // the same thing wherever it appears) plus the metal tones a bezel is
  // actually made in and the neutrals a plain steel bezel needs. `two-tone` is
  // NOT a value here — that is what selecting two colors expresses; a `mixed`
  // slug exists on lookup_bezel_material instead, where the single-select
  // shape genuinely needs one.
  //
  // Auto-seeds the lookup_bezel_colors doc the first time it's opened/read.
  // Deliberately a STARTER list, not a finished vocabulary — James curates it
  // in the Lookup Manager, and values added there are never overwritten by
  // this seed (it only fires when the Firestore doc is missing entirely).
  // Case body colours. Seeded from the shared colour vocabulary plus the
  // materials a CASE is actually made in — resin, titanium and bronze appear
  // here and not on a bezel insert. Deliberately a STARTER list: James curates
  // it in the Lookup Manager and values added there are never overwritten by
  // this seed, which only fires when the Firestore doc is missing entirely.
  lookup_case_colors: [
    { value: 'black', displayName: 'Black' },
    { value: 'silver', displayName: 'Silver' },
    { value: 'steel', displayName: 'Steel' },
    { value: 'gray', displayName: 'Gray', synonyms: ['grey'] },
    { value: 'white', displayName: 'White' },
    { value: 'gold', displayName: 'Gold' },
    { value: 'rose-gold', displayName: 'Rose Gold' },
    { value: 'yellow-gold', displayName: 'Yellow Gold' },
    { value: 'titanium', displayName: 'Titanium' },
    { value: 'bronze', displayName: 'Bronze' },
    { value: 'blue', displayName: 'Blue' },
    { value: 'green', displayName: 'Green' },
    { value: 'red', displayName: 'Red' },
    { value: 'yellow', displayName: 'Yellow' },
    { value: 'orange', displayName: 'Orange' },
    { value: 'brown', displayName: 'Brown' },
    { value: 'beige', displayName: 'Beige', synonyms: ['sand', 'tan'] },
    { value: 'khaki', displayName: 'Khaki' },
    { value: 'pink', displayName: 'Pink' },
    { value: 'purple', displayName: 'Purple' },
  ],
  lookup_bezel_colors: [
    { value: 'black', displayName: 'Black' },
    { value: 'blue', displayName: 'Blue' },
    { value: 'red', displayName: 'Red' },
    { value: 'green', displayName: 'Green' },
    { value: 'brown', displayName: 'Brown' },
    { value: 'gray', displayName: 'Gray', synonyms: ['grey'] },
    { value: 'white', displayName: 'White' },
    { value: 'silver', displayName: 'Silver' },
    { value: 'gold', displayName: 'Gold' },
    { value: 'rose-gold', displayName: 'Rose Gold' },
    { value: 'yellow', displayName: 'Yellow' },
    { value: 'orange', displayName: 'Orange' },
    { value: 'pink', displayName: 'Pink' },
    { value: 'purple', displayName: 'Purple' },
    { value: 'burgundy', displayName: 'Burgundy' },
    { value: 'bronze', displayName: 'Bronze' },
    { value: 'steel', displayName: 'Steel' },
  ],
  // Index/marker colors. Same comma-joined multi-select convention as
  // hand_colors (a dial often mixes colors, e.g. gold indices + white print).
  // No `lume` value here — that lives with the Phase 2 luminova work.
  // Auto-seeds the lookup_index_colors doc the first time it's opened/read.
  lookup_index_colors: [
    { value: 'white', displayName: 'White' },
    { value: 'black', displayName: 'Black' },
    { value: 'gold', displayName: 'Gold' },
    { value: 'rose-gold', displayName: 'Rose Gold' },
    { value: 'silver', displayName: 'Silver' },
    { value: 'blue', displayName: 'Blue' },
    { value: 'green', displayName: 'Green' },
    { value: 'red', displayName: 'Red' },
  ],
  // Functional hand TYPES present on the dial (function, not style — style lives
  // in lookup_dial_hands). Multi-select, comma-joined like hand_colors.
  // Auto-seeds the lookup_hand_types doc the first time it's opened/read.
  lookup_hand_types: [
    { value: 'hour', displayName: 'Hour' },
    { value: 'minute', displayName: 'Minute' },
    { value: 'seconds', displayName: 'Seconds' },
    { value: 'sub-seconds', displayName: 'Sub-Seconds' },
    { value: 'gmt', displayName: 'GMT' },
    { value: 'date', displayName: 'Date' },
    { value: 'day', displayName: 'Day' },
    { value: 'power-reserve', displayName: 'Power Reserve' },
    { value: 'chronograph-second', displayName: 'Chronograph Second' },
    { value: 'chronograph-minute', displayName: 'Chronograph Minute' },
    { value: 'chronograph-hour', displayName: 'Chronograph Hour' },
  ],
  // Acquisition TYPE — the kind of brand acquisition / lineage link. Values are
  // the AcquisitionType union (src/types) and the seed CSV's `acquisition_type`
  // column. value === enum slug so the BrandAcquisition.acquisitionType field
  // round-trips. Auto-seeds the lookup_acquisition_types doc on first read.
  // `rnReverseLabel` is the label the RN app renders on the REVERSE side of the
  // relationship ("X is [reverseLabel] Y", where X is the acquired brand and Y
  // the acquirer) — the bidirectional counterpart to each type's displayName.
  // `name_change` carries the `uses-former-name` property: the AcquisitionsEditor
  // reads it to swap the brand picker + directness for a "Former name" input.
  lookup_acquisition_types: [
    {
      value: 'full_brand_merger',
      displayName: 'Full Brand Merger',
      rnReverseLabel: 'Merged with',
    },
    {
      value: 'full_brand_company_acquisition',
      displayName: 'Full Brand / Company Acquisition',
      rnReverseLabel: 'Acquired by',
    },
    {
      value: 'brand_rights_acquisition',
      displayName: 'Brand Rights Acquisition',
      rnReverseLabel: 'Rights held by',
    },
    {
      value: 'manufacture_integration',
      displayName: 'Manufacture Integration',
      rnReverseLabel: 'Manufacture absorbed into',
    },
    {
      value: 'brand_acquisition_for_revival',
      displayName: 'Brand Acquisition for Revival',
      rnReverseLabel: 'Revived by',
    },
    {
      value: 'rights_acquisition_for_revival',
      displayName: 'Rights Acquisition for Revival',
      rnReverseLabel: 'Rights revived by',
    },
    {
      value: 'successor_renaming_absorption',
      displayName: 'Successor Renaming / Absorption',
      rnReverseLabel: 'Predecessor of',
    },
    {
      value: 'brand_and_product_takeover',
      displayName: 'Brand and Product Takeover',
      rnReverseLabel: 'Took over from',
    },
    {
      value: 'name_change',
      displayName: 'Name change',
      rnReverseLabel: 'Renamed to',
      properties: ['uses-former-name'],
    },
  ],
  // Acquisition DIRECTNESS — how direct the link is. Values are the
  // AcquisitionDirectness union (src/types) and the seed CSV's `directness`
  // column. Auto-seeds the lookup_acquisition_directness doc on first read.
  lookup_acquisition_directness: [
    {
      value: 'direct_watch_brand_to_watch_brand',
      displayName: 'Direct Watch Brand → Watch Brand',
    },
    {
      value: 'watch_brand_company_to_watch_brand',
      displayName: 'Watch Brand Company → Watch Brand',
    },
    {
      value: 'assigned_to_watch_brand_after_group_acquisition',
      displayName: 'Assigned After Group Acquisition',
      rnQualifier: 'via parent group',
    },
    {
      value: 'brand_ecosystem_not_pure_direct',
      displayName: 'Brand Ecosystem (not pure direct)',
      rnQualifier: 'via ownership ecosystem',
    },
    {
      value: 'successor_brand_lineage',
      displayName: 'Successor Brand Lineage',
      rnQualifier: 'rename, not acquisition',
    },
    {
      value: 'same_brand_revival',
      displayName: 'Same Brand Revival',
      rnQualifier: 'same brand, new owners',
    },
  ],
  // Movement decoration techniques (multi-select). Seeded by the AdminConfig
  // "Seed Movement Decoration Lookup" button via reseedMovementDecorationLookup.
  // Curated vocab James extends over time; the AI enrichment (decorations[])
  // may also propose techniques not yet listed here. rhodium/gilt are
  // deliberately absent — they have dedicated CalibreData booleans
  // (rhodiumPlated / giltFinish) so they stay individually facetable.
  lookup_movement_decoration: [
    { value: 'perlage', displayName: 'Perlage', synonyms: ['circular graining'] },
    { value: 'cotes_de_geneve', displayName: 'Côtes de Genève', synonyms: ['geneva stripes', 'geneva waves'] },
    { value: 'anglage', displayName: 'Anglage', synonyms: ['bevelling', 'chamfering'] },
    { value: 'blued_screws', displayName: 'Blued Screws', synonyms: ['blue screws'] },
    { value: 'engraving', displayName: 'Engraving', synonyms: ['hand-engraving'] },
    { value: 'guilloche', displayName: 'Guilloché', synonyms: ['guilloche'] },
    { value: 'sunburst', displayName: 'Sunburst', synonyms: ['soleil'] },
    { value: 'snailing', displayName: 'Snailing', synonyms: ['colimaçon'] },
    { value: 'black_polishing', displayName: 'Black Polishing', synonyms: ['specular polishing'] },
    { value: 'frosting', displayName: 'Frosting', synonyms: ['frosted'] },
  ],

  // ---------------------------------------------------------------------
  // Materials & plate finishing (shared-types v1.92.0). Starter vocabularies
  // only — enough that the {{LOOKUP_*}} tokens in the enrichCalibre prompt
  // render a real slug list on the first run, rather than an empty one the AI
  // can't pick from. James extends these in the Lookup Manager; the seed is
  // non-destructive, so an admin-added value survives a re-seed.
  //
  // Gold is split by colour rather than carried as one `gold` slug: the colour
  // is the part a spec sheet states, and collapsing it would lose information
  // no other field records. Precious-metal purity stays in the slug (18k) for
  // the same reason.
  // ---------------------------------------------------------------------

  // What the oscillating weight is made of. Heavy metals dominate because rotor
  // mass IS the winding efficiency — hence tungsten on high-end automatics and
  // gold on precious-metal executions, where the mass is a bonus on top of the
  // decorative point.
  lookup_rotor_material: [
    { value: 'steel', displayName: 'Steel', synonyms: ['stainless steel'] },
    { value: 'brass', displayName: 'Brass' },
    { value: 'tungsten', displayName: 'Tungsten', synonyms: ['tungsten carbide', 'heavy metal'] },
    { value: '18k_rose_gold', displayName: '18k Rose Gold', synonyms: ['18k red gold', '18k pink gold'] },
    { value: '18k_yellow_gold', displayName: '18k Yellow Gold' },
    { value: '18k_white_gold', displayName: '18k White Gold' },
    { value: '22k_gold', displayName: '22k Gold', synonyms: ['22ct gold'] },
    { value: 'platinum', displayName: 'Platinum' },
    { value: 'titanium', displayName: 'Titanium' },
  ],

  // What the bridges (and a shared mainplate) are made of. German silver is the
  // marker of a Glashütte-school movement; rhodium-plated brass is the Swiss
  // default and is recorded here as `brass` plus the `rhodiumPlated` boolean,
  // since the plating is a treatment rather than the material.
  lookup_bridge_material: [
    { value: 'brass', displayName: 'Brass', synonyms: ['rhodium-plated brass', 'gilt brass'] },
    { value: 'german_silver', displayName: 'German Silver', synonyms: ['maillechort', 'nickel silver (untreated)'] },
    { value: 'nickel_silver', displayName: 'Nickel Silver', synonyms: ['argentan'] },
    { value: 'steel', displayName: 'Steel', synonyms: ['stainless steel'] },
    { value: 'titanium', displayName: 'Titanium' },
    { value: '18k_rose_gold', displayName: '18k Rose Gold', synonyms: ['18k red gold'] },
    { value: '18k_yellow_gold', displayName: '18k Yellow Gold' },
    { value: '18k_white_gold', displayName: '18k White Gold' },
    { value: 'platinum', displayName: 'Platinum' },
  ],

  // The mainplate's defining surface treatment — ONE per calibre. The two
  // `*_mainplate` slugs deliberately duplicate technique names that also exist
  // in lookup_movement_decoration: there the technique is "present somewhere on
  // the movement", here it is "this is what the mainplate looks like", and the
  // suffix keeps the two readable side by side in the editor.
  lookup_plate_finishing: [
    { value: 'sunburst', displayName: 'Sunburst', synonyms: ['soleil', 'sunray'] },
    { value: 'frosted', displayName: 'Frosted', synonyms: ['frosting', 'grained'] },
    { value: 'sandblasted', displayName: 'Sandblasted', synonyms: ['bead-blasted', 'microblasted'] },
    { value: 'perlage_mainplate', displayName: 'Perlage (Mainplate)', synonyms: ['circular graining'] },
    { value: 'cotes_de_geneve_mainplate', displayName: 'Côtes de Genève (Mainplate)', synonyms: ['geneva stripes'] },
    { value: 'snailed', displayName: 'Snailed', synonyms: ['colimaçon', 'circular graining (snailed)'] },
    { value: 'straight_grained', displayName: 'Straight-Grained', synonyms: ['brushed', 'linear graining'] },
    { value: 'polished', displayName: 'Polished', synonyms: ['mirror-polished'] },
    { value: 'galvanic_coated', displayName: 'Galvanic Coated', synonyms: ['galvanic treatment', 'plated finish'] },
  ],

  // Self-winding system topology — WHERE the winding mass sits and how it
  // moves. Only shown on the calibre editor when the selected movement type
  // carries the `isAuto` property. Deliberately kept separate from the free-text
  // `rotorType` field, which describes the rotor's bearing/suspension or
  // material (ball-bearing, jewelled bearing, tungsten, 22k gold, …).
  // "hammer" is a synonym on `bumper` rather than a value of its own — they
  // name the same mechanism. Re-seedable from the AdminConfig "Seed Auto
  // Winding Architecture Lookup" button via reseedWindingArchitectureLookup.
  lookup_winding_architecture: [
    {
      value: 'full_rotor',
      displayName: 'Full Rotor (Central Rotor)',
      synonyms: ['central rotor', 'full-rotor'],
    },
    {
      value: 'micro_rotor',
      displayName: 'Micro Rotor',
      synonyms: ['microrotor', 'micro-rotor'],
    },
    {
      value: 'peripheral_rotor',
      displayName: 'Peripheral Rotor',
      synonyms: ['peripheral winding', 'peripheral oscillating weight'],
    },
    {
      value: 'bumper',
      displayName: 'Bumper Automatic',
      synonyms: ['hammer', 'hammer automatic', 'bumper'],
    },
    {
      value: 'dual_rotor',
      displayName: 'Dual Rotor',
      synonyms: ['twin rotor', 'double rotor'],
    },
    {
      value: 'oscillating_weight',
      displayName: 'Oscillating Weight (generic/unknown)',
      synonyms: ['oscillating mass'],
    },
  ],

  // ── Storage & Watch Winders (Task #30) ──────────────────────────────────────

  // The 8 storage categories. Values are the StorageLocationCategory union from
  // @watchkeeper/shared-types (v1.101.0 added `at_service`) — the slug IS the
  // enum member, so the RN picker round-trips what it writes to
  // Watch.storageLocation. ARRAY order is the canonical union order
  // (STORAGE_LOCATION_CATEGORIES) and a test pins it; DISPLAY order comes from
  // `sortOrder`, and the two disagree on purpose — see the note on `at_service`.
  // Editing a slug here breaks the type contract — add/rename in shared-types
  // first. Re-seedable from the AdminConfig "Seed Storage & Winder Lookups"
  // button.
  //
  // ⚠️ MIRRORS LIVE FIRESTORE, and mirrors watchlock's fallback block in
  // `LookupCollectionInitializer.ts` (#1023). These values only surface on a
  // cold start or in a test environment, so their whole job is to be
  // indistinguishable from the seeded collection. Drift here is a divergence
  // nobody sees until the environment that uses them behaves differently.
  //
  // `properties` is an OPT-IN flag list — the same mechanism as
  // `showDial`/`isBracelet` on lookup_watch_types, so it needs no shared-types
  // change. Absent means false, so a category carrying none is simply an
  // on-site place with no appliance and no paperwork:
  //
  //   isOffSite           the watch is NOT physically with its owner. Gates wear
  //                       tracking and rotation candidacy downstream — you
  //                       cannot be wearing a watch that is in a bank vault, in
  //                       the post, or on a watchmaker's bench.
  //   hasAppliance        the category links to a catalogued appliance instance
  //                       (a winder, a safe OR a display case — see
  //                       WinderEntry.applianceKind).
  //   hasLocationDetails  the category offers the free-text
  //                       StorageLocationDetails block. On where there is no
  //                       catalogue entity to answer "where is this and who do I
  //                       ask" — a vault, a bench, a loan. Off for
  //                       winder/display_case/worn, which are your own home or
  //                       your own wrist. The first two have an appliance to
  //                       answer it instead.
  lookup_storage_locations: [
    {
      value: 'bank_vault',
      displayName: 'Bank Vault',
      synonyms: ['safe deposit box', 'safety deposit box', 'bank safe'],
      properties: ['isOffSite', 'hasLocationDetails'],
    },
    {
      value: 'personal_safe',
      displayName: 'Personal Safe',
      synonyms: ['home safe', 'gun safe', 'safe'],
      properties: ['hasAppliance', 'hasLocationDetails'],
    },
    {
      value: 'winder',
      displayName: 'Watch Winder',
      synonyms: ['automatic winder', 'watch winder'],
      properties: ['hasAppliance'],
    },
    {
      value: 'display_case',
      displayName: 'Display Case',
      synonyms: ['watch box', 'vitrine', 'display box'],
      // Live gained this flag before the code shipped, which is the mechanism
      // working as designed — turning a category on is a Lookup Manager edit,
      // not a PR. Mirrored here so a cold-started RN client agrees.
      //
      // No `hasLocationDetails`, for the same reason `winder` has none: the
      // appliance answers "where is this and who do I ask", or the answer is
      // your own home.
      properties: ['hasAppliance'],
    },
    { value: 'worn', displayName: 'Worn', synonyms: ['on wrist', 'wearing'] },
    {
      value: 'in_transit',
      displayName: 'In Transit',
      // 'at service' and 'being repaired' USED to sit here, and moved to
      // at_service when that category was added — a watch on a workbench is not
      // a watch in the post, and the synonyms are what the RN picker's search
      // matches on.
      synonyms: ['shipping', 'travelling'],
      properties: ['isOffSite', 'hasLocationDetails'],
    },
    {
      value: 'at_service',
      displayName: 'At Service',
      synonyms: ['Watch Maker', 'Service', 'at service', 'being repaired'],
      properties: ['isOffSite', 'hasLocationDetails'],
      // 8, not 7. Added to the live collection AFTER `other`, so it took the
      // next free position rather than being slotted in beside `in_transit`.
      // Mirrored rather than corrected — see the sortOrder note on the seed
      // type, and the ⚠️ above about array order and sortOrder disagreeing.
      sortOrder: 8,
    },
    { value: 'other', displayName: 'Other', properties: ['hasLocationDetails'], sortOrder: 7 },
  ],

  // Winder makes. A STARTER vocabulary, not a finished one — James curates it in
  // the Lookup Manager and anything added there survives a re-seed. `make` on a
  // WinderEntry stores the slug, so a value's slug is what the winder doc id is
  // built from: renaming a slug here orphans existing winders, renaming only the
  // displayName is safe.
  lookup_winder_manufacturers: [
    { value: 'wolf', displayName: 'Wolf', synonyms: ['wolf designs', 'wolf 1834'] },
    { value: 'barrington', displayName: 'Barrington', synonyms: ['barrington watch winders'] },
    { value: 'orbita', displayName: 'Orbita', synonyms: ['orbita corporation'] },
    { value: 'buben-zorweg', displayName: 'Buben & Zörweg', synonyms: ['buben and zorweg', 'buben & zorweg', 'b&z'] },
    { value: 'rapport', displayName: 'Rapport', synonyms: ['rapport london'] },
    { value: 'scatola-del-tempo', displayName: 'Scatola del Tempo', synonyms: ['scatola'] },
  ],

  // Rotation directions. These slugs are the WinderEntry.rotationDirection union
  // — the field is typed in shared-types, so this list is NOT free vocabulary.
  // Adding a value here that isn't in the union produces a doc the RN app can't
  // narrow; extend the union in shared-types first.
  lookup_winder_rotation_directions: [
    {
      value: 'clockwise',
      displayName: 'Clockwise',
      synonyms: ['cw', 'clockwise only'],
    },
    {
      value: 'counter_clockwise',
      displayName: 'Counter-clockwise',
      synonyms: ['ccw', 'anti-clockwise', 'anticlockwise', 'counterclockwise'],
    },
    {
      value: 'bidirectional',
      displayName: 'Bi-directional',
      synonyms: ['bi-directional', 'both directions', 'alternating'],
    },
  ],

  // Winding programs / cycle modes. MULTI — a programmable winder advertises
  // several at once. Describes HOW the winder cycles, not how many turns it
  // makes: the turns-per-day settings live in WinderEntry.turnsPerDay as real
  // numbers, so no TPD value belongs in this vocabulary.
  lookup_winder_programs: [
    {
      value: 'continuous',
      displayName: 'Continuous Rotation',
      synonyms: ['non-stop', 'constant rotation'],
    },
    {
      value: 'intermittent',
      displayName: 'Intermittent Cycle',
      synonyms: ['duty cycle', 'rotate and rest', 'cyclic'],
    },
    {
      value: 'rest-day',
      displayName: 'Rest Day',
      synonyms: ['rest period', 'pause day'],
    },
    {
      value: 'sleep-cycle',
      displayName: 'Sleep Cycle',
      synonyms: ['sleep mode', 'night mode', 'silent hours'],
    },
    {
      value: 'custom-tpd',
      displayName: 'Custom TPD',
      synonyms: ['adjustable tpd', 'programmable turns per day'],
    },
    {
      value: 'programmable-direction',
      displayName: 'Programmable Direction',
      synonyms: ['selectable direction', 'direction setting'],
    },
  ],

  // Power sources. MULTI — dual-supply (mains with battery backup) is the norm
  // above the entry tier, which is exactly what selecting two values expresses.
  // There is deliberately no `dual-ac-battery` slug: that is `ac` + `batteries`.
  lookup_winder_power_sources: [
    { value: 'ac', displayName: 'AC Mains', synonyms: ['mains', 'plug-in', 'ac adapter', 'wall power'] },
    { value: 'batteries', displayName: 'Batteries', synonyms: ['battery', 'aa batteries', 'd cell'] },
    { value: 'usb', displayName: 'USB', synonyms: ['usb-c', 'micro usb', 'usb powered'] },
    { value: 'rechargeable-battery', displayName: 'Rechargeable Battery', synonyms: ['built-in battery', 'internal battery', 'li-ion'] },
    { value: 'solar', displayName: 'Solar', synonyms: ['solar powered'] },
  ],

  // Case / build materials. MULTI — a winder is routinely several at once (a
  // lacquered wood case with a glass lid and a leather interior).
  lookup_winder_materials: [
    { value: 'wood', displayName: 'Wood', synonyms: ['timber', 'walnut', 'ebony', 'burl'] },
    { value: 'lacquer', displayName: 'Lacquer', synonyms: ['lacquered', 'piano finish', 'high gloss'] },
    { value: 'leather', displayName: 'Leather', synonyms: ['genuine leather', 'calfskin'] },
    { value: 'faux-leather', displayName: 'Faux Leather', synonyms: ['pu leather', 'vegan leather', 'leatherette'] },
    { value: 'carbon-fibre', displayName: 'Carbon Fibre', synonyms: ['carbon fiber', 'carbon'] },
    { value: 'aluminium', displayName: 'Aluminium', synonyms: ['aluminum', 'anodised aluminium'] },
    { value: 'stainless-steel', displayName: 'Stainless Steel', synonyms: ['steel', 'inox'] },
    { value: 'glass', displayName: 'Glass', synonyms: ['tempered glass', 'glass lid'] },
    { value: 'acrylic', displayName: 'Acrylic', synonyms: ['perspex', 'plexiglass', 'plastic'] },
    { value: 'fabric', displayName: 'Fabric', synonyms: ['velvet', 'suede', 'microfibre', 'microfiber'] },
  ],

  // Market positioning band. Deliberately a coarse tier rather than a price:
  // prices move, currencies differ, and the catalog is world-readable.
  lookup_winder_price_tiers: [
    { value: 'entry', displayName: 'Entry', synonyms: ['budget', 'affordable'] },
    { value: 'mid', displayName: 'Mid-range', synonyms: ['mid-range', 'midrange'] },
    { value: 'premium', displayName: 'Premium', synonyms: ['high-end'] },
    {
      value: 'ultra-premium',
      displayName: 'Ultra-premium',
      synonyms: ['luxury', 'ultra luxury', 'safe-grade'],
    },
  ],
};

// Helper function to get config for a collection
export function getLookupCollectionConfig(
  collectionName: string,
): LookupCollectionConfig | undefined {
  return LOOKUP_COLLECTIONS_CONFIG.find(
    (c) => c.collectionName === collectionName,
  );
}

// Helper function to get group by ID
export function getLookupGroup(groupId: string): LookupGroup | undefined {
  return LOOKUP_GROUPS.find((g) => g.id === groupId);
}

// Helper function to get all collections in a group
export function getCollectionsByGroup(
  groupId: string,
): LookupCollectionConfig[] {
  return LOOKUP_COLLECTIONS_CONFIG.filter((c) => c.groupId === groupId);
}

// Helper function to get groups with their collections
export function getGroupedCollections(): {
  group: LookupGroup;
  collections: LookupCollectionConfig[];
}[] {
  return LOOKUP_GROUPS.map((group) => ({
    group,
    collections: getCollectionsByGroup(group.id),
  }))
    .filter((g) => g.collections.length > 0)
    .sort((a, b) => a.group.sortOrder - b.group.sortOrder);
}
